// BM-H398 - s2a_joint_assets. Properties live once in
// `couple.properties_register`; `property_selections[]` carries the
// engine-read `severance_decision` and `severance_service` per property.
//
// SEVERANCE_JOINT_DECISION_GUIDANCE renders as block paragraphs immediately
// above the severance decision, before the answer is taken, every time the
// question is shown. Never a tooltip, never behind a disclosure.

import BlockCopyView from "./BlockCopyView";
import { SEVERANCE_JOINT_DECISION_GUIDANCE } from "./copySentinels";
import { MoneyInput, NumberInput, QuestionRow, RepeatList, SectionIntro, Select, TextInput, YesNo, sectionProps } from "./fields";
import PersonPicker from "./PersonPicker";
import { newProperty } from "./willsPayload";

export const SEVERANCE_DECISION_OPTIONS = [
  { value: "keep_as_joint_tenants", label: "Keep it as it is, passing automatically to the survivor" },
  { value: "sever_as_part_of_instruction", label: "Sever the joint tenancy so my share passes under my will" },
  { value: "discuss", label: "We are unsure, or we do not agree. Please talk it through with us" },
];

const SEVERANCE_SERVICE_OPTIONS = [
  { value: "yes", label: "Yes, please arrange the severance for us" },
  { value: "no", label: "No, we will arrange it ourselves" },
  { value: "discuss", label: "I would like to discuss this" },
];

function PropertyEditor({ property, onChange, payload, update, index, testator }) {
  const tid = `s2a-property-${index}`;
  const coOwner = property.co_owners?.find((c) => c.person_id !== testator?.id)?.person_id || null;
  return (
    <div>
      <QuestionRow qid="property.address_line1" label="Address (first line)" testid={`${tid}-q-address`}>
        <TextInput testid={`${tid}-address`} value={property.address?.line1} onChange={(v) => onChange({ ...property, address: { ...property.address, line1: v } })} />
      </QuestionRow>
      <QuestionRow qid="property.postcode" label="Postcode" testid={`${tid}-q-postcode`}>
        <TextInput testid={`${tid}-postcode`} value={property.address?.postcode} onChange={(v) => onChange({ ...property, address: { ...property.address, postcode: v } })} />
      </QuestionRow>
      <QuestionRow qid="property.address_country" label="Country (only if the property is outside the United Kingdom)" testid={`${tid}-q-country`}>
        <TextInput testid={`${tid}-country`} value={property.address?.country} onChange={(v) => onChange({ ...property, address: { ...property.address, country: v } })} />
      </QuestionRow>
      <QuestionRow qid="property.approx_value_gbp" label="Rough value" testid={`${tid}-q-value`}>
        <MoneyInput testid={`${tid}-value`} value={property.approx_value_gbp} onChange={(v) => onChange({ ...property, approx_value_gbp: v })} />
      </QuestionRow>
      <QuestionRow qid="property.mortgage_outstanding" label="Is there a mortgage?" testid={`${tid}-q-mortgage`}>
        <YesNo testid={`${tid}-mortgage`} value={property.mortgage_outstanding} onChange={(v) => onChange({ ...property, mortgage_outstanding: v })} />
      </QuestionRow>
      <QuestionRow qid="property.is_main_home" label="Is this the home you live in?" testid={`${tid}-q-main-home`}>
        <YesNo testid={`${tid}-main-home`} value={property.is_main_home} onChange={(v) => onChange({ ...property, is_main_home: v })} />
      </QuestionRow>
      <QuestionRow qid="property.ownership" label="Do you own it alone or with someone else?" testid={`${tid}-q-ownership`}>
        <Select testid={`${tid}-ownership`} value={property.ownership} options={[{ value: "sole", label: "Alone" }, { value: "joint", label: "With someone else" }]} onChange={(v) => onChange({ ...property, ownership: v, co_owners: v === "joint" ? property.co_owners : [], joint_form: v === "joint" ? property.joint_form : null })} />
      </QuestionRow>
      {property.ownership === "joint" && (
        <>
          <PersonPicker payload={payload} update={update} qid="property.co_owner_id" label="Who owns it with you?" value={coOwner} exclude={[testator?.id]} onChange={(pid) => onChange({ ...property, co_owners: [{ person_id: testator?.id }, ...(pid ? [{ person_id: pid }] : [])] })} testid={`${tid}-co-owner`} defaultRelationship="spouse" />
          <QuestionRow qid="property.joint_form" label="How do you hold it together?" evidence testid={`${tid}-q-joint-form`}>
            <Select testid={`${tid}-joint-form`} value={property.joint_form} options={[{ value: "joint_tenants", label: "Joint tenants" }, { value: "tenants_in_common", label: "Tenants in common" }, { value: "not_known", label: "Not sure, please check the title" }]} onChange={(v) => onChange({ ...property, joint_form: v })} />
          </QuestionRow>
          {property.joint_form === "tenants_in_common" && (
            <QuestionRow qid="property.tic_share_percent" label="Your share (percent)" testid={`${tid}-q-tic-share`}>
              <NumberInput testid={`${tid}-tic-share`} value={property.tic_share_percent} onChange={(v) => onChange({ ...property, tic_share_percent: v })} />
            </QuestionRow>
          )}
        </>
      )}
    </div>
  );
}

export default function S2aJointAssets({ section, setSection, payload, update, person }) {
  const { s, set } = sectionProps(section, setSection);
  const properties = payload.couple.properties_register || [];
  const selections = s.property_selections || [];
  const selectionFor = (pid) => selections.find((x) => x.property_id === pid) || { property_id: pid, severance_decision: null, severance_service: null };
  const setSelection = (pid, patch) => {
    const next = selections.some((x) => x.property_id === pid)
      ? selections.map((x) => (x.property_id === pid ? { ...x, ...patch } : x))
      : [...selections, { ...selectionFor(pid), ...patch }];
    set({ property_selections: next });
  };

  function addProperty() {
    const prop = newProperty({ is_main_home: properties.length === 0 });
    update((d) => { d.couple.properties_register.push(prop); });
    set({ owns_property: "yes" });
  }
  function editProperty(i, next) {
    update((d) => { d.couple.properties_register[i] = next; });
  }
  function removeProperty(i) {
    const pid = properties[i].id;
    update((d) => { d.couple.properties_register = d.couple.properties_register.filter((x) => x.id !== pid); });
    set({ property_selections: selections.filter((x) => x.property_id !== pid) });
  }

  return (
    <div data-testid="section-s2a_joint_assets">
      <SectionIntro title="Your home and property">
        Tell us about your home and any other property you own, alone or with someone else. Addresses and rough figures are all we need.
      </SectionIntro>
      <QuestionRow qid="s2a_joint_assets.owns_property" label="Do you own any property?">
        <YesNo testid="s2a-owns-property" value={s.owns_property === null ? null : s.owns_property === "yes"} onChange={(v) => set({ owns_property: v ? "yes" : "no" })} />
      </QuestionRow>

      {s.owns_property === "yes" && (
        <RepeatList
          testid="s2a-properties"
          items={properties}
          onAdd={addProperty}
          onRemove={removeProperty}
          addLabel="Add a property"
          emptyText="No property added yet."
          render={(prop, i) => (
            <>
              <PropertyEditor property={prop} onChange={(next) => editProperty(i, next)} payload={payload} update={update} index={i} testator={person} />
              {prop.ownership === "joint" && prop.joint_form === "joint_tenants" && (
                <div data-testid={`s2a-severance-${i}`}>
                  <BlockCopyView copy={SEVERANCE_JOINT_DECISION_GUIDANCE} testid={`severance-guidance-${i}`} />
                  <QuestionRow qid="s2a_joint_assets.property_selections.severance_decision" label="What would you both like to do about the joint tenancy on this property?" testid={`s2a-q-severance-decision-${i}`}>
                    <Select testid={`s2a-severance-decision-${i}`} value={selectionFor(prop.id).severance_decision} options={SEVERANCE_DECISION_OPTIONS} onChange={(v) => setSelection(prop.id, { severance_decision: v, severance_service: v === "discuss" ? "discuss" : v === "sever_as_part_of_instruction" ? selectionFor(prop.id).severance_service : null })} />
                  </QuestionRow>
                  {selectionFor(prop.id).severance_decision === "sever_as_part_of_instruction" && (
                    <>
                      <QuestionRow qid="s2a_joint_assets.property_selections.severance_service" label="Would you like us to arrange the severance for you?" testid={`s2a-q-severance-service-${i}`}>
                        <Select testid={`s2a-severance-service-${i}`} value={selectionFor(prop.id).severance_service} options={SEVERANCE_SERVICE_OPTIONS} onChange={(v) => setSelection(prop.id, { severance_service: v })} />
                      </QuestionRow>
                      {/* BM-H3365 - title number only, not the severance-completion
                          date. The completion date needs to be tied to an
                          evidenced completion step in the matter workflow ("a
                          decision merely to sever is not proof that severance
                          has occurred", per the ticket) - that is a product/
                          workflow design question, not a field, and is left for
                          a separate piece of work. Title number alone still
                          gives the fee earner real, useful progress: one of the
                          two pieces C-09a/C-09b need to be drafted by hand. */}
                      <QuestionRow qid="property.title_number" label="Do you know the property's title number?" testid={`s2a-q-title-number-${i}`}>
                        <TextInput testid={`s2a-title-number-${i}`} value={prop.title_number} onChange={(v) => editProperty(i, { ...prop, title_number: v })} />
                      </QuestionRow>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        />
      )}
    </div>
  );
}
